import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import TabGroup from "../../components/TabGroup/TabGroup";
import Favourite from "../../components/AppAccount/Favourite";
import Dialog from "../appMode/Dialog";
import style from "./FavouritePage.module.scss";
import {
  clearFavourites,
  DRUG_INDEX_FAVOURITES_STORAGE_KEY,
  getFavourites,
  saveFavourite,
  TOPIC_FAVOURITES_STORAGE_KEY,
} from "../../components/FavouriteButton/favouritesService";

const TOPIC_FAVOURITES_TAB_ID = "topics";
const DRUG_INDEX_FAVOURITES_TAB_ID = "drug-index";
const TOPIC_FAVOURITES_LIST_LABEL = "topic favourites";
const DRUG_INDEX_FAVOURITES_LIST_LABEL = "drug index favourites";

const TabLabel = ({ label, count }) => (
  <span className={style.tabLabel}>
    {label}
    {count > 0 && <span className={style.countBadge}>{count}</span>}
  </span>
);

const FavouriteList = ({ emptyFavouritesMessage, storageKey, onCountChange }) => {
  const [favourites, setFavourites] = useState(() => getFavourites(storageKey));
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const favouritesListLabel =
    storageKey === DRUG_INDEX_FAVOURITES_STORAGE_KEY
      ? DRUG_INDEX_FAVOURITES_LIST_LABEL
      : TOPIC_FAVOURITES_LIST_LABEL;

  const refreshFavourites = () => {
    const updated = getFavourites(storageKey);
    setFavourites(updated);
    onCountChange?.(storageKey, updated.length);
  };

  const removeFavourite = (title, url, type, guideline) => {
    saveFavourite(title, url, type, guideline, undefined, storageKey);
    refreshFavourites();
  };

  const clearAllFavourites = () => {
    clearFavourites(storageKey);
    refreshFavourites();
    setConfirmDialogOpen(false);
  };

  return (
    <>
      {favourites.length > 0 && (
        <div className={style.clearAllActions}>
          <button className={style.clearAllButton} onClick={() => setConfirmDialogOpen(true)}>
            Clear all
          </button>
        </div>
      )}
      {favourites.length > 0 ? (
        <div className={style.favouriteList}>
          {favourites.map((favourite, index) => (
            <Favourite
              title={favourite.favouriteTitle}
              url={favourite.favouriteUrl}
              guideline={favourite.topicSection?.replace("/", "")}
              type={favourite.type}
              sectionId={favourite.sectionId}
              removeFavourite={removeFavourite}
              key={`favId-${storageKey}-${index}`}
            />
          ))}
        </div>
      ) : (
        <div className={style.emptyTabLine}>{emptyFavouritesMessage}</div>
      )}
      <Dialog
        bodyText={`Are you sure you want to clear all items from your ${favouritesListLabel} list? This action cannot be undone.`}
        confirmFunction={clearAllFavourites}
        heading="Confirmation"
        buttonText="Clear all"
        isOpen={confirmDialogOpen}
        setIsOpen={setConfirmDialogOpen}
      />
    </>
  );
};

const FavouritesTabGroup = () => {
  const [searchParams] = useSearchParams();
  const defaultTabId =
    searchParams.get("tab") === DRUG_INDEX_FAVOURITES_TAB_ID
      ? DRUG_INDEX_FAVOURITES_TAB_ID
      : TOPIC_FAVOURITES_TAB_ID;

  const [counts, setCounts] = useState({
    [TOPIC_FAVOURITES_STORAGE_KEY]: getFavourites(TOPIC_FAVOURITES_STORAGE_KEY).length,
    [DRUG_INDEX_FAVOURITES_STORAGE_KEY]: getFavourites(DRUG_INDEX_FAVOURITES_STORAGE_KEY).length,
  });

  const handleCountChange = (storageKey, count) => {
    setCounts((prev) => ({ ...prev, [storageKey]: count }));
  };

  const TopicFavouritesTab = () => (
    <FavouriteList
      emptyFavouritesMessage="You have no topic favourites."
      storageKey={TOPIC_FAVOURITES_STORAGE_KEY}
      onCountChange={handleCountChange}
    />
  );

  const DrugIndexFavouritesTab = () => (
    <FavouriteList
      emptyFavouritesMessage="You have no drug index favourites."
      storageKey={DRUG_INDEX_FAVOURITES_STORAGE_KEY}
      onCountChange={handleCountChange}
    />
  );

  const favouriteTabs = [
    {
      id: TOPIC_FAVOURITES_TAB_ID,
      label: <TabLabel label="Topics" count={counts[TOPIC_FAVOURITES_STORAGE_KEY]} />,
      component: TopicFavouritesTab,
    },
    {
      id: DRUG_INDEX_FAVOURITES_TAB_ID,
      label: <TabLabel label="Drugs" count={counts[DRUG_INDEX_FAVOURITES_STORAGE_KEY]} />,
      component: DrugIndexFavouritesTab,
    },
  ];

  return (
    <TabGroup
      tabs={favouriteTabs}
      defaultId={defaultTabId}
      className={style.segmentedControl}
      tabButtonClassName={style.pillTab}
      activeTabButtonClassName={style.pillTabActive}
    />
  );
};

export default FavouritesTabGroup;
